import { useEffect, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  Skeleton,
  TextField,
  Typography,
} from '@mui/material';
import { Box } from '@mui/system';
import { GridExpandMoreIcon } from '@mui/x-data-grid';

import {
  ContractEntry,
  PostTemplateContractAttachmentsResponse,
  AdmissionKitDocuments,
} from '@octopus/api';
import { contractTypeLabels, workerCategoryLabels } from '@octopus/contract-types';
import { getWorkerCategory } from '@octopus/esocial/contracts';

import { SnackbarType } from '../../../hooks/snackbarContext';
import { useSnackbar } from '../../../hooks/useSnackbar';

import { MobileDrawer, NoTemplatesDialog } from '.';

type TemplateSummary = {
  templateId: string;
  name: string;
  category: string;
  active: boolean;
};

const errorSnackbar: SnackbarType = {
  isOpen: true,
  variant: 'error',
  Message: 'Ocorreu um erro, por favor tente novamente.',
  hasCloseAction: true,
};

export function GenerateAdmissionKitDialog({
  open,
  setOpen,
  action,
  templates,
  loadingTemplates,
  templatesError,
  contract,
  draftId,
  admissionKitDocuments,
  generateKitLoadingMessage,
}: {
  open: boolean;
    setOpen: ({
      open,
      contractId,
      draftId,
      admissionKitDocuments,
    }: {
      open: boolean;
      contractId: string;
      draftId: string;
        admissionKitDocuments: AdmissionKitDocuments
    }) => void;
  action: (
    {
      templateIds,
      contract,
      draftId,
    }: {
        templateIds: string[],
        contract: ContractEntry,
        draftId: string,
      }
  ) => Promise<PostTemplateContractAttachmentsResponse>;
  templates: TemplateSummary[];
  loadingTemplates: boolean;
  templatesError: string | null;
    contract: ContractEntry | null;
    draftId: string;
    admissionKitDocuments: AdmissionKitDocuments;
    generateKitLoadingMessage: string | null;
}) {
  const contractId = contract?.contractId;
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<TemplateSummary[]>(
    [],
  );
  const [showNoTemplatesDialog, setShowNoTemplatesDialog] = useState(false);
  const [notFoundTags, setNotFoundTags] = useState<string[]>([]);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    if (open) {
      setSelectedTemplates([]);
      setExpanded(true);
      if (!loadingTemplates && templates.length === 0) {
        setOpen({ open: false, contractId, draftId, admissionKitDocuments });
        setShowNoTemplatesDialog(true);
      }

      if (templates.length > 0 && admissionKitDocuments?.length > 0) {
        setSelectedTemplates(templates.filter((template) => admissionKitDocuments.some((document) => document.templateId === template.templateId)));
      }
    }
  }, [open, templates, loadingTemplates]);

  const handleError = (error: Error) => {
    showSnackbar(errorSnackbar);
    setOpen({ open: false, contractId, draftId, admissionKitDocuments });
    setLoading(false);
    console.error(error);
  };

  const handleGenerateKit = async () => {
    if (loading === true) {
      return; // avoid multiple clicks
    }
    try {
      setLoading(true);

      const templateIds = selectedTemplates.map(
        (template) => template.templateId,
      );
      const documentsWithnotFoundTags = await action({ templateIds, contract, draftId });
      if (documentsWithnotFoundTags.length > 0) {
        const notFoundTags = [
          ...new Set(
            documentsWithnotFoundTags.flatMap(
              (document) => document.notFoundTags || [],
            ),
          ),
        ];
        setNotFoundTags(notFoundTags);
        setExpanded(false);
      } else {
        setOpen({ open: false, contractId, draftId, admissionKitDocuments });
        setNotFoundTags([]);
      }
    } catch (error) {
      setExpanded(false);
      console.error('Erro ao gerar documentos:', error);
      handleError(
        error instanceof Error ? error : new Error('Erro desconhecido'),
      );
    } finally {
      setLoading(false);
    }
  };

  const trabalho =
    contract?.br && 'trabalho' in contract.br
      ? contract?.br?.trabalho
      : undefined;

  const duracao =
    contract?.br && 'duracao' in contract.br
      ? contract?.br?.duracao
      : undefined;
  const workerCategory = getWorkerCategory(contract?.contractType, trabalho);
  const workerCategoryLabel = contract ? `${workerCategoryLabels[workerCategory]} (${contractTypeLabels[contract?.contractType]})` : '';

  const content = (
    <Box
      display={'flex'}
      flexDirection={'column'}
      alignItems={'flex-start'}
      borderRadius={2}
      px={1.5}
      gap={2}
      boxSizing={'border-box'}
    >
      <Typography variant={'h2'} fontWeight={700}>
        Documentos admissionais
      </Typography>


      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={(theme) => ({
          width: '100%',
          borderRadius: 1,
          border: `1px solid ${theme.palette.strokes.light}`,
          boxShadow: 'none',
          "&::before": {
            display: "none"
          },
          "&.Mui-expanded": {
            marginTop: 0,
            marginBottom: 0,
          },
          '&.Mui-expanded::before': {
            display: 'none',
          },
        })}>
        <AccordionSummary expandIcon={<GridExpandMoreIcon />} >
          <Typography variant='body2'>
            <b>Informações relevantes</b>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          {!contract && (
            <Skeleton variant="text" width="100%" height="100px" />
          )}
          {contract && (
            <Box sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              boxSizing: 'border-box',
            }}>
              <InfoBox leftText="Modalidade de contratação" rightText={workerCategoryLabel} />
              <InfoBox leftText="Número de dependentes" rightText={contract.br?.dependentes?.length?.toString() ?? '0'} />
              <InfoBox leftText="Número de dependentes com salário família" rightText={contract.br?.dependentes?.filter(
                (dep) => 'depSF' in dep && dep.depSF,
              )?.length?.toString() ?? '0'} />
              <InfoBox leftText="Período de experiência" rightText={duracao?.dtPerExpTerm ? 'Sim' : 'Não'} />
              <InfoBox leftText="Cargo de confiança" rightText={trabalho?.CBOFuncao ? 'Sim' : 'Não'} />
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      <Box display="flex" flexDirection="column" gap={1} width="100%">
        <Typography variant="caption" color="text.secondary">
          Selecione os documentos a serem gerados
        </Typography>
        <FormControl fullWidth error={!!templatesError}>
          <Autocomplete
            multiple
            disableCloseOnSelect
            id="template-select"
            options={templates}
            loading={loadingTemplates}
            disabled={loadingTemplates || templates.length === 0}
            getOptionLabel={(option) => option.name}
            value={selectedTemplates}
            onChange={(_, newValue) => {
              setSelectedTemplates(newValue);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  {...getTagProps({ index })}
                  deleteIcon={<CloseIcon />}
                  size="small"
                  sx={{
                    display: 'flex',
                    minHeight: '18px',
                    maxHeight: '18px',
                    alignItems: 'center',
                    borderRadius: '100px',
                    backgroundColor: 'rgba(37, 37, 45, 0.04)',
                    '& .MuiChip-label': {
                      color: 'text.primary',
                      fontSize: '12px',
                      lineHeight: '12px',
                      letterSpacing: '0.15px',
                    },
                    '& .MuiChip-deleteIcon': {
                      fontSize: '12px',
                      fontWeight: 500,
                      color: 'text.secondary',
                      margin: '0 4px 0 -4px',
                    },
                  }}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder='Selecione'
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingTemplates ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              />
            )}
            noOptionsText="Nenhum template disponível"
            loadingText="Carregando templates..."
          />
          {templatesError && <FormHelperText>{templatesError}</FormHelperText>}
        </FormControl>
      </Box>

      {notFoundTags?.length > 0 && (
        <Alert severity="warning" sx={{ gap: 1 }}>
          <Typography variant="h4" color="warning.main">
            Ação manual necessária
          </Typography>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Typography variant="body2" color="warning.main">
              Os documentos foram gerados, mas os seguintes campos ficaram em
              branco, verifique os arquivos gerados e preencha os campos
              manualmente.
            </Typography>
            <Typography variant="body2" color="warning.main">
              {notFoundTags.join(', ')}
            </Typography>
          </Box>
        </Alert>
      )}
    </Box>
  );

  const actions = (
    <>
      {!notFoundTags || notFoundTags?.length <= 0 && (
        <>
          <Button
            variant="outlined"
            color="secondary"
            sx={{
              px: 4,
              ':hover': {
                backgroundColor: 'strokes.light',
              },
            }}
            size="large"
            onClick={() => {
              setOpen({ open: false, contractId, draftId, admissionKitDocuments });
              setNotFoundTags([]);
            }}
            data-testid="cancel-generate-kit-button"
            disabled={loading}
          >
            Cancelar
          </Button>
          <Button
            color="primaryAlt"
            variant="contained"
            size="large"
            onClick={handleGenerateKit}
            disabled={
              loadingTemplates ||
              templates.length === 0 ||
              selectedTemplates.length === 0 ||
              notFoundTags.length > 0
            }
          >
            {loading ? generateKitLoadingMessage : 'Avançar'}
            {loading && <CircularProgress size={20} sx={{ ml: 3, color: 'currentColor' }} />}
          </Button>
        </>
      )}
      {
        notFoundTags?.length > 0 && (
          <Button
            variant="contained"
            color="primaryAlt"
            size="large"
            onClick={() => {
              setOpen({ open: false, contractId, draftId, admissionKitDocuments });
              setNotFoundTags([]);
              setExpanded(true);
            }}
          >
            Entendi
          </Button>
        )
      }
    </>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={() => setOpen({ open: false, contractId, draftId, admissionKitDocuments })}
        sx={(theme) => ({
          [theme.breakpoints.down('sm')]: { display: 'none' },
        })}
        PaperProps={{
          sx: {
            minWidth: '600px',
            maxWidth: '600px',
            minHeight: '450px',
          },
        }}
      >
        <DialogContent>{content}</DialogContent>
        <DialogActions>{actions}</DialogActions>
      </Dialog>
      <MobileDrawer
        open={open}
        setOpen={(open) => setOpen({ open, contractId, draftId, admissionKitDocuments })}
        actions={actions}
        content={content}
        setLoading={setLoading}
      />
      <NoTemplatesDialog
        open={showNoTemplatesDialog}
        setOpen={setShowNoTemplatesDialog}
      />
    </>
  );
}

const InfoBox = ({ leftText, rightText }: { leftText: string, rightText: string }) => {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', width: '100%', justifyContent: 'space-between' }}>
      <Typography variant="caption" color="text.secondary">
        {leftText}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {rightText}
      </Typography>
    </Box>
  );
};
